// Site-wide themes (WS-3 phase 2).
//
// A theme is a `[data-theme="…"]` token-override block in styles/colors.css —
// components only consume semantic vars, so switching the attribute re-skins
// the whole app. Three base themes are free for everyone; store `map_theme`
// items double as full site skins when equipped (their heat ramp keeps
// applying to the map exactly as before).
//
// Resolution order (applied in AppShell): equipped store skin, else the
// user's stored base choice, else dark. The base choice shares the
// `atlas-theme` localStorage key with the landing-page light/dark toggle.

export interface FreeTheme {
  slug: string;
  name: string;
  /** [bg, surface, accent] – used for the swatch preview in the drawer. */
  swatch: [string, string, string];
}

export const FREE_THEMES: FreeTheme[] = [
  { slug: "dark", name: "Dark", swatch: ["#090C10", "#161D26", "#F4B740"] },
  { slug: "light", name: "Light", swatch: ["#F3F1EA", "#FFFFFF", "#E0A026"] },
  { slug: "forest", name: "Forest", swatch: ["#070D09", "#12211A", "#37BE76"] },
];

/** Store item slug → data-theme value for items that re-skin the site. */
export const STORE_THEME_SKINS: Record<string, string> = {
  "alpenglow-ramp": "alpenglow",
  "spruce-ramp": "spruce",
  "monochrome-ink": "mono",
  "heather-ramp": "heather",
  "north-sea-ramp": "north-sea",
  "ember-ramp": "ember",
  "ordnance-ramp": "ordnance",
  "aurora-ramp": "aurora",
};

const STORAGE_KEY = "atlas-theme";

/** The user's chosen base theme (free themes only — store skins live on the
 * equipped mapTheme slot, not in localStorage). */
export function storedBaseTheme(): string {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return FREE_THEMES.some((t) => t.slug === v) ? (v as string) : "dark";
  } catch {
    return "dark";
  }
}

export function setBaseTheme(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* noop */
  }
}

export function applySiteTheme(slug: string): void {
  document.documentElement.dataset["theme"] = slug;
}
